import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
//
import { doAccessAction } from 'store/actions/role-list.actions';
import { ROLES, API_METHODS } from 'config/constants';
import { getUserRoleLabel } from 'utils/business-logic/getUserRoleFromGroupsList';
import { Loader } from 'components';
import { BUS_USER_IS_BLOCKED } from 'config/error-codes-dictionary';
import ComplexSuccessIcon from 'components/SvgIcons/ComplexIcons/success.svg';
import ComplexCrossIcon from 'components/SvgIcons/ComplexIcons/cross.svg';
import { MODAL_TYPES } from '../constants';
import { NotesForm } from './NotesForm';
import { getConnectDescription } from './getConnectionDescription';
import { sendFormData } from 'store/actions/step-registration.actions';
import { isSponsorInitiator } from './SponsorForm/isSponsorInitiator';
import { SponsorForm } from './SponsorForm';
import { change } from 'redux-form';
import {
  mapStudiesToOptions,
  mapCountriesForStudy,
} from './parseStudiesCountries';
import { buildResearchSiteConnectBody } from './buildEisfAccessBody';

@connect(
  (
    {
      roleList: { processing = {}, sponsorProfileRequests = {} },
      authState: {
        authInfo: {
          groups = [],
          personnelTypes = [],
          currentOrganizationId,
        } = {},
      } = {},
    },
    props,
  ) => ({
    processing: processing[`connect-${props.options.userId}`],
    sponsorProfileRequests,
    groups,
    personnelTypes,
    currentOrganizationId,
  }),
  (dispatch) => ({
    doAccessAction: bindActionCreators(doAccessAction, dispatch),
    sendFormData: bindActionCreators(sendFormData, dispatch),
    dispatch,
  }),
)
export class ConnectModal extends React.PureComponent {
  static propTypes = {
    doAccessAction: PropTypes.func,
    options: PropTypes.shape({
      userRole: PropTypes.string,
      userId: PropTypes.string,
      listDataKey: PropTypes.string,
      actionCb: PropTypes.func,
    }),
    groups: PropTypes.array,
  };

  static defaultProps = {
    options: {},
  };

  state = {
    studiesCountries: [],
    studiesList: [],
    countriesList: [],
  };

  componentDidMount() {
    const {
      groups,
      personnelTypes,
      currentOrganizationId,
      options: { userRole } = {},
    } = this.props;

    if (
      isSponsorInitiator({
        groups,
        personnelTypes,
        recipientRole: userRole,
        currentOrganizationId,
      })
    ) {
      this.loadSponsorStudies();
    }
  }

  loadSponsorStudies = async () => {
    const response = await this.props.sendFormData(
      API_METHODS.INTEGRATION_STUDIES_COUNTRIES,
      {},
      'get',
    );

    const items = response?.data?.items ?? response?.items ?? [];

    this.setState({
      studiesCountries: items,
      studiesList: mapStudiesToOptions(items),
      countriesList: [],
    });
  };

  handleStudyChange = (studyId) => {
    const { studiesCountries } = this.state;

    this.setState({
      countriesList: mapCountriesForStudy(studiesCountries, studyId),
    });

    this.props.dispatch?.(change('sponsorForm', 'countryId', null));
  };

  handleSubmit = async (values) => {
    const {
      showModal,
      doAccessAction,
      options: {
        userRole,
        userId,
        listDataKey,
        actionCb,
        handleReloadPage,
      } = {},
    } = this.props;
    let url;
    let requestMethod = 'post';

    switch (userRole) {
      case ROLES.SPONSOR:
        url = API_METHODS.ACCESS_REQUEST_SPONSOR;
        break;
      case ROLES.RESEARCH_SITE:
        url = API_METHODS.ACCESS_REQUEST_RESEARCH_SITE;
        break;
      case ROLES.INACTIVE_RESEARCH_SITE:
        url = API_METHODS.INACTIVE_RESEARCH_INVITE(userId);
        requestMethod = 'put';
        break;
      case ROLES.INACTIVE_SPONSOR:
        url = API_METHODS.INACTIVE_SPONSOR_INVITE(userId);
        requestMethod = 'put';
        break;
      default:
        throw new Error('Unknown user role');
    }

    const body =
      url === API_METHODS.ACCESS_REQUEST_RESEARCH_SITE
        ? buildResearchSiteConnectBody(values, userId)
        : { ...values, recipientId: userId };

    const response = await doAccessAction({
      requestMethod,
      url,
      body,
      userId,
      processingKey: `connect-${userId}`,
      stateKeyForDataSaving: listDataKey,
      rewriteAccessRequestKey: 'id',
    });

    if (!response || response.code !== 'success') {
      const { errors = [] } = response;
      const isBlocked = errors.find(
        (item) => item.code === BUS_USER_IS_BLOCKED,
      );

      if (isBlocked) {
        return showModal(MODAL_TYPES.BLOCKED, { reason: isBlocked.message });
      }

      return showModal(MODAL_TYPES.INFO, {
        withCross: false,
        icon: <ComplexCrossIcon />,
        text: 'Ooops….something went wrong.',
        body: 'Your request is not sent. Please try again later.',
        additionalClassName: 'p-15',
      });
    }

    showModal(MODAL_TYPES.INFO, {
      withCross: false,
      icon: <ComplexSuccessIcon />,
      text: 'Done. Your request has been sent.',
      body: 'You can track the status of the request in your sent requests dashboard.',
      additionalClassName: 'p-15',
      handleReloadPage: handleReloadPage,
    });
    actionCb && actionCb();
  };

  render() {
    const {
      options: { userRole } = {},
      groups,
      personnelTypes,
      currentOrganizationId,
    } = this.props;

    const showSponsorForm = isSponsorInitiator({
      groups,
      personnelTypes,
      recipientRole: userRole,
      currentOrganizationId,
    });

    const roleLabel = getUserRoleLabel(userRole);

    return (
      <Loader isShow={this.props.processing}>
        <div
          className="white-box p-15 text-left"
          style={{ maxWidth: '550px' }}
          data-testid="connect-modal"
        >
          <div className="montserrat bold fs-16">{`Connect with ${roleLabel}`}</div>
          <div className="fs-12 mt-2">{getConnectDescription(userRole)}</div>
          {showSponsorForm ? (
            <SponsorForm
              studiesList={this.state.studiesList}
              countriesList={this.state.countriesList}
              onStudyChange={this.handleStudyChange}
              onSubmit={this.handleSubmit}
            />
          ) : (
            <NotesForm
              notesPlaceholder={`Add notes for ${roleLabel}`}
              onSubmit={this.handleSubmit}
            />
          )}
        </div>
      </Loader>
    );
  }
}
